import { Headphones, Plus, Edit, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getDownloadURL, ref } from 'firebase/storage';
import Button from '../../components/Button';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import CopyButton from '../../components/CopyButton';
import EmptyState from '../../components/EmptyState';
import InlineErrorDisplay from '../../components/InlineErrorDisplay';
import LoadingSpinner from '../../components/LoadingSpinner';
import { storage } from '../../firebase';
import { audioGuidesService } from '../../services/audioGuidesService';
import type { AudioGuide } from '../../types/audio-guides';
import { AudioGuideModal } from './components/AudioGuideModal';

export const AudioGuides = () => {
    const [audioGuides, setAudioGuides] = useState<AudioGuide[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [imageUrls, setImageUrls] = useState<Record<string, string>>({});

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [selectedGuide, setSelectedGuide] = useState<AudioGuide | null>(null);

    // Delete confirmation state
    const [deleteConfirm, setDeleteConfirm] = useState<{
        show: boolean;
        guide: AudioGuide | null;
    }>({ show: false, guide: null });

    // Convert gs:// URI to download URL
    const getDownloadUrlFromPath = async (path: string): Promise<string> => {
        try {
            if (!path) return '';

            // If already an HTTP URL, return as-is
            if (path.startsWith('http://') || path.startsWith('https://')) {
                return path;
            }

            // Firebase getDownloadURL handles gs:// URIs directly
            const storageRef = ref(storage, path);
            const url = await getDownloadURL(storageRef);
            return url;
        } catch (err) {
            console.error(`Failed to get download URL for path: ${path}`, err);
            return '';
        }
    };

    const loadAudioGuides = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const guides = await audioGuidesService.getAllAudioGuides();
            setAudioGuides(guides);

            // Load download URLs for all images
            const urlsMap: Record<string, string> = {};
            await Promise.all(
                guides.map(async (guide) => {
                    if (guide.bannerPath) {
                        const url = await getDownloadUrlFromPath(guide.bannerPath);
                        urlsMap[guide.uid] = url;
                    }
                })
            );
            setImageUrls(urlsMap);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load audio guides');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadAudioGuides();
    }, [loadAudioGuides]);

    const getStatusBadgeStyles = (status?: 'draft' | 'published') => {
        if (status === 'published') {
            return 'bg-green-100 text-green-800';
        }
        return 'bg-gray-100 text-gray-800';
    };

    const handleNewGuide = () => {
        setSelectedGuide(null);
        setShowModal(true);
    };

    const handleEditGuide = (guide: AudioGuide) => {
        setSelectedGuide(guide);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedGuide(null);
    };

    const handleSave = () => {
        loadAudioGuides();
    };

    const handleDeleteClick = (guide: AudioGuide) => {
        setDeleteConfirm({ show: true, guide });
    };

    const handleDeleteConfirm = async () => {
        if (!deleteConfirm.guide) return;

        try {
            await audioGuidesService.deleteAudioGuide(deleteConfirm.guide.uid);
            setDeleteConfirm({ show: false, guide: null });
            loadAudioGuides();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete audio guide');
            setDeleteConfirm({ show: false, guide: null });
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirm({ show: false, guide: null });
    };

    const exportMarkdown = useMemo(() => {
        const lines: string[] = ['# Audio Guides', ''];

        audioGuides.filter(guide => guide.status === 'published').forEach((guide, index) => {
            lines.push(`## ${index + 1}. ${guide.title}`);
            lines.push(`- **ID:** ${guide.uid}`);
            lines.push(`- **Free:** ${guide.isFree ? 'Yes' : 'No'}`);
            if (guide.aiGuidance) {
                lines.push(`- **AI Guidance:** ${guide.aiGuidance}`);
            }
            lines.push('');
        });

        return lines.join('\n');
    }, [audioGuides]);

    if (loading) {
        return <LoadingSpinner message="Loading audio guides..." />;
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex flex-wrap justify-between items-start gap-4">
                        <div className="flex-1">
                            <h1 className="text-3xl font-bold text-gray-900">Audio Guides</h1>
                            <p className="mt-2 text-gray-600">Manage audio guides and tools</p>
                        </div>
                        <div className="flex flex-wrap items-center gap-3">
                            <CopyButton
                                text={exportMarkdown}
                                label="Export Markdown"
                                disabled={audioGuides.length === 0}
                            />
                            <Button
                                onClick={handleNewGuide}
                                variant="primary"
                                icon={<Plus size={20} />}
                            >
                                New Audio Guide
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Error Display */}
                {error && (
                    <InlineErrorDisplay
                        message={error}
                        onDismiss={() => setError(null)}
                    />
                )}

                {/* Audio Guides List */}
                <div className="space-y-4">
                    {audioGuides.length === 0 ? (
                        <div className="bg-white rounded-lg shadow-md p-8">
                            <EmptyState
                                icon={<Headphones size={48} className="mx-auto text-gray-400 mb-4" />}
                                title="No audio guides found"
                                description="Create your first audio guide to get started."
                                action={{
                                    label: "Create Audio Guide",
                                    onClick: handleNewGuide,
                                    variant: "primary"
                                }}
                            />
                        </div>
                    ) : (
                        audioGuides.map((guide) => (
                            <div key={guide.uid} className="bg-white rounded-lg shadow-md p-4 sm:p-6">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                    <div className="flex items-center gap-4 flex-1 min-w-0">
                                        {/* Guide Image */}
                                        {imageUrls[guide.uid] ? (
                                            <img
                                                src={imageUrls[guide.uid]}
                                                alt={guide.title}
                                                className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                                                onError={(e) => {
                                                    // Hide broken images and show placeholder
                                                    e.currentTarget.style.display = 'none';
                                                    const placeholder = e.currentTarget.nextElementSibling;
                                                    if (placeholder) {
                                                        (placeholder as HTMLElement).style.display = 'flex';
                                                    }
                                                }}
                                            />
                                        ) : null}
                                        <div className={`w-16 h-16 rounded-lg bg-gray-200 items-center justify-center flex-shrink-0 ${imageUrls[guide.uid] ? 'hidden' : 'flex'}`}>
                                            <Headphones className="text-gray-400" size={32} />
                                        </div>

                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-lg font-semibold text-gray-900 break-words">{guide.title}</h3>
                                            <p className="text-gray-600 text-sm break-words">{guide.bannerText}</p>
                                            <div className="flex flex-wrap items-center gap-3 mt-2">
                                                <span className={`text-xs px-2 py-1 rounded font-medium ${getStatusBadgeStyles(guide.status)}`}>
                                                    {guide.status || 'draft'}
                                                </span>
                                                <span className="text-xs text-gray-500">Order: {guide.order || 0}</span>
                                                {guide.isFree ? (
                                                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Free</span>
                                                ) : (
                                                    <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">Premium</span>
                                                )}
                                                <span className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-1 rounded break-all">
                                                    ID: {guide.uid}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <Button
                                            onClick={() => handleEditGuide(guide)}
                                            variant="primary"
                                            size="sm"
                                            icon={<Edit size={16} />}
                                        >
                                            Edit
                                        </Button>
                                        <Button
                                            onClick={() => handleDeleteClick(guide)}
                                            variant="danger"
                                            size="sm"
                                            icon={<Trash2 size={16} />}
                                        >
                                            Delete
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Audio Guide Modal */}
            <AudioGuideModal
                audioGuide={selectedGuide}
                isOpen={showModal}
                onClose={handleCloseModal}
                onSave={handleSave}
            />

            {/* Delete Confirmation Dialog */}
            <ConfirmationDialog
                isOpen={deleteConfirm.show}
                title="Delete Audio Guide"
                message={`Are you sure you want to delete "${deleteConfirm.guide?.title}"? This action cannot be undone.`}
                confirmText="Delete"
                cancelText="Cancel"
                confirmVariant="danger"
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
            />
        </div>
    );
};
